/**
 * Temu 履约部署区（与后端 TemuShopSiteCatalog 对齐）。
 * 美区 / 欧区 / 全球；运费·仓库接口不可混区。
 */

import { flattenSiteRows } from '@/temu-platform/utils/temuSiteList';
import { fixedUi } from '@/i18n/fixedUi';
import { localizedSiteLabel } from '@/i18n/catalogDisplayText';

export const DEPLOYMENT_REGIONS = Object.freeze([
  { key: 'US', label: '美区' },
  { key: 'EU', label: '欧区' },
  { key: 'GLOBAL', label: '全球' },
]);

/** 美区：仅美国站 */
const US_SITE_IDS = new Set([100]);

/** 泛欧 27 + 欧区补充站（英国等） */
const EU_SITE_IDS = new Set([
  105, 106, 107, 108, 109, 111, 112, 113, 115, 116, 117, 141, 137, 138,
  139, 140, 142, 143, 144, 145, 146, 147, 148, 149, 150, 151, 152,
  102, 114, 124, 153, 154, 155, 156, 158, 159, 192, 197,
]);

/** 官方已开站目录（id → 中文名），顺序与后端一致。 */
const OPEN_SITE_ENTRIES = Object.freeze([
  [100, '美国站'],
  [101, '加拿大站'],
  [102, '英国站'],
  [103, '澳大利亚站'],
  [104, '新西兰站'],
  [105, '德国站'],
  [106, '法国站'],
  [107, '意大利站'],
  [108, '荷兰站'],
  [109, '西班牙站'],
  [110, '墨西哥站'],
  [111, '葡萄牙站'],
  [112, '波兰站'],
  [113, '瑞典站'],
  [114, '瑞士站'],
  [115, '希腊站'],
  [116, '爱尔兰站'],
  [117, '塞浦路斯站'],
  [118, '日本站'],
  [120, '沙特站'],
  [122, '阿联酋站'],
  [123, '科威特站'],
  [124, '挪威站'],
  [125, '智利站'],
  [126, '马来西亚站'],
  [127, '菲律宾站'],
  [129, '泰国站'],
  [130, '卡塔尔站'],
  [131, '约旦站'],
  [133, '阿曼站'],
  [134, '巴林站'],
  [135, '以色列站'],
  [136, '南非站'],
  [137, '捷克站'],
  [138, '匈牙利站'],
  [139, '丹麦站'],
  [140, '罗马尼亚站'],
  [141, '保加利亚站'],
  [142, '比利时站'],
  [143, '奥地利站'],
  [144, '芬兰站'],
  [145, '斯洛伐克站'],
  [146, '克罗地亚站'],
  [147, '斯洛文尼亚站'],
  [148, '立陶宛站'],
  [149, '爱沙尼亚站'],
  [150, '拉脱维亚站'],
  [151, '马耳他站'],
  [152, '卢森堡站'],
  [153, '塞尔维亚站'],
  [154, '摩尔多瓦站'],
  [155, '黑山站'],
  [156, '冰岛站'],
  [158, '波黑站'],
  [159, '阿尔巴尼亚站'],
  [163, '秘鲁站'],
  [164, '哥伦比亚站'],
  [165, '格鲁吉亚站'],
  [166, '亚美尼亚站'],
  [167, '阿塞拜疆站'],
  [170, '毛里求斯站'],
  [171, '摩洛哥站'],
  [176, '巴拿马站'],
  [178, '厄瓜多尔站'],
  [185, '斯里兰卡站'],
  [187, '越南站'],
  [188, '文莱站'],
  [192, '北马其顿站'],
  [194, '吉尔吉斯斯坦站'],
  [197, '列支敦士登站'],
]);

const OPEN_SITE_NAME_BY_ID = new Map(OPEN_SITE_ENTRIES);

export function regionOfSiteId(siteId) {
  const id = Number(siteId);
  if (!Number.isFinite(id)) return 'GLOBAL';
  if (US_SITE_IDS.has(id)) return 'US';
  if (EU_SITE_IDS.has(id)) return 'EU';
  return 'GLOBAL';
}

export function nameOfSiteId(siteId) {
  const id = Number(siteId);
  if (!Number.isFinite(id)) return '';
  return OPEN_SITE_NAME_BY_ID.get(id) || '';
}

function parseSiteId(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * 店铺信息「售卖站点」：按美区 / 欧区 / 全球分组。
 * 有本店站点缓存时用缓存；否则用官方已开站目录（替代原「全站点」一行）。
 *
 * @returns {{ key: string, label: string, sites: { siteId: number|null, siteName: string }[] }[]}
 */
export function buildDeploymentRegionGroups(siteList) {
  const rows = flattenSiteRows(siteList);
  /** @type {{ siteId: number|null, siteName: string }[]} */
  let sites = [];

  if (rows.length > 0) {
    const seen = new Set();
    rows.forEach((row) => {
      const siteId = parseSiteId(row.siteAbbr);
      const dedupeKey = siteId != null ? `id:${siteId}` : `name:${row.siteName}`;
      if (seen.has(dedupeKey)) return;
      seen.add(dedupeKey);
      const catalogName = siteId != null ? nameOfSiteId(siteId) : '';
      const rawName = String(row.siteName || '').trim()
        || catalogName
        || (siteId != null ? `${fixedUi('站点')} ${siteId}` : '—');
      const siteName = rawName === '—' ? rawName : localizedSiteLabel(rawName);
      sites.push({ siteId, siteName });
    });
  } else {
    sites = OPEN_SITE_ENTRIES.map(([siteId, siteName]) => ({
      siteId,
      siteName: localizedSiteLabel(siteName),
    }));
  }

  const buckets = {
    US: [],
    EU: [],
    GLOBAL: [],
  };
  sites.forEach((site) => {
    const region = site.siteId != null ? regionOfSiteId(site.siteId) : 'GLOBAL';
    buckets[region].push(site);
  });

  return DEPLOYMENT_REGIONS
    .map((region) => ({
      key: region.key,
      label: fixedUi(region.label),
      sites: buckets[region.key],
    }))
    .filter((group) => group.sites.length > 0);
}

/**
 * 产品模板是否属于指定部署区（按 selectedSites / siteAbbr 归属美区·欧区·全球）。
 * @param {object} row
 * @param {string} gatewayRegion ALL|US|EU|GLOBAL
 */
export function templateMatchesGatewayRegion(row, gatewayRegion) {
  const region = String(gatewayRegion || '').trim().toUpperCase();
  if (!region || region === 'ALL') return true;
  const ids = collectTemplateSiteIds(row);
  if (ids.length === 0) {
    // 未选站点的模板：仅在「全部」可见
    return false;
  }
  return ids.some((id) => regionOfSiteId(id) === region);
}

/**
 * 产品模板上架站点中文名（优先店铺/官方目录名，其次内置开站目录）。
 * @param {object} row
 * @param {unknown} [catalogSites] 店铺或官方站点列表
 * @returns {string[]}
 */
export function formatTemplateSiteNames(row, catalogSites) {
  const ids = collectTemplateSiteIds(row);
  if (!ids.length) return [];
  const nameById = new Map();
  flattenSiteRows(catalogSites).forEach((site) => {
    const id = parseSiteId(site.siteAbbr);
    if (id == null) return;
    const name = String(site.siteName || '').trim();
    if (name && name !== '—') nameById.set(id, name);
  });
  return ids.map((id) => {
    const raw = nameById.get(id) || nameOfSiteId(id) || '';
    return raw ? localizedSiteLabel(raw) : `${fixedUi('站点')} ${id}`;
  });
}

function collectTemplateSiteIds(row) {
  const out = [];
  const seen = new Set();
  const push = (raw) => {
    const id = parseSiteId(raw);
    if (id == null || seen.has(id)) return;
    seen.add(id);
    out.push(id);
  };
  const sites = row?.selectedSites;
  if (Array.isArray(sites)) {
    sites.forEach((item) => {
      if (item == null) return;
      if (typeof item === 'string' || typeof item === 'number') {
        push(item);
        return;
      }
      push(item.siteAbbr ?? item.siteId ?? item.abbr ?? item.site);
    });
  }
  push(row?.siteAbbr);
  push(row?.siteId);
  if (typeof row?.selectedSitesJson === 'string' && row.selectedSitesJson.trim()) {
    try {
      const parsed = JSON.parse(row.selectedSitesJson);
      if (Array.isArray(parsed)) {
        parsed.forEach((item) => {
          if (typeof item === 'string' || typeof item === 'number') push(item);
          else if (item && typeof item === 'object') {
            push(item.siteAbbr ?? item.siteId ?? item.abbr ?? item.site);
          }
        });
      }
    } catch {
      /* ignore */
    }
  }
  return out;
}
